import { DeferredPage } from "@/components/data-display/deferred-page";
import { PermissionDeniedState } from "@/components/ui/system-state";
import { canAccessAdmin } from "@/lib/permissions";
import { useAuthStore } from "@/stores/auth-store";

// Deferred (page-delivery-pipeline.md G6, 2026-09-23). A support-bundle domain exists in
// alloy-diagnostics-service but only the gateway route is missing — this pass is
// frontend-only and cannot add it. See context/page_pipeline/BACKEND_GAPS.md row 27.
//
// Entry gate (ALL-5219). This route rendered the deferred state to every role, including roles that
// can never reach admin tooling. It is now shown only to a session the admin portal itself admits —
// `canAccessAdmin(role)`, the same check the app switcher uses to offer the Admin app — and everyone
// else gets the standard permission-denied system state. This is not a route guard: the app shell has
// no route guards (tools/synthetic-customer-lab/fixtures/ux-affordances.json -> gating_model), so the
// page renders the denied state itself, exactly as the wallet-request page does. There is no
// support-bundle permission to gate on instead: Permission.java's auth.admin_support_access.manage is
// required by no gateway route, and no support-bundle route exists at all, so the role name is the
// only real authority available here.
export default function AdminSupportBundlePage() {
  const role = useAuthStore((state) => state.user?.role);

  if (!canAccessAdmin(role)) {
    return (
      <PermissionDeniedState
        title="You don't have access to support bundles"
        description="Creating and downloading a diagnostic support bundle is limited to organization admins. Ask an organization admin if you need access."
      />
    );
  }

  return (
    <DeferredPage
      nav={{ kind: "header", title: "Support Bundle", subtitle: "Create and download a diagnostic support bundle for Alloy support." }}
      state={{
        title: "Support bundles are not available in this environment yet",
        description: "Creating and downloading a support bundle needs a gateway route that has not been added yet.",
      }}
    />
  );
}
